
import { useState, useRef, useCallback } from "react";
import { Message } from "../ChatMessage";
import { sendMessageToAssistant } from "../utils/messageUtils";
import { handleNetworkError } from "../utils/chatNetworkUtils";
import { isOnline } from "@/utils/connectionUtils";
import { handleError, ErrorSeverity } from "@/utils/errorHandler";
import { retryOperation } from "@/utils/connectionUtils";

export const useMessageHandler = (
  messages: Message[],
  setMessages: (messages: Message[]) => void,
  messageIdCounter: React.MutableRefObject<number>,
  user?: any
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [networkError, setNetworkError] = useState(false);
  const lastMessageRef = useRef<string>("");

  const handleSendMessage = useCallback(async (userMessage: string) => {
    if (!userMessage.trim() && !lastMessageRef.current) return;

    const messageToSend = userMessage.trim() || lastMessageRef.current;
    lastMessageRef.current = messageToSend;

    if (retryAttempt === 0) {
      const userMessageId = messageIdCounter.current++;
      setMessages([...messages, {
        id: userMessageId,
        role: "user",
        content: messageToSend
      }]);
    }

    setIsLoading(true);
    setNetworkError(false);

    try {
      if (!isOnline()) {
        throw new Error("You appear to be offline. Please check your connection.");
      }

      const contextMessages = messages.slice(-5).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const result = await retryOperation(
        async () => sendMessageToAssistant(messageToSend, contextMessages, user?.user_metadata?.role),
        retryAttempt > 0 ? 1 : 2
      );

      setRetryAttempt(0);
      lastMessageRef.current = "";

      const responseMessageId = messageIdCounter.current++;
      setMessages([...messages, {
        id: responseMessageId,
        role: "assistant",
        content: result.answer
      }]);
    } catch (error) {
      console.error("Error calling help assistant:", error);
      
      if (!isOnline()) {
        handleNetworkError(setNetworkError);
        handleError(
          error,
          "Network connection issue",
          ErrorSeverity.WARNING,
          { operation: "helpAssistant", additionalInfo: { offline: true } },
          false
        );
      } else {
        setRetryAttempt(prev => prev + 1);
        
        if (retryAttempt === 0) {
          const errorMessageId = messageIdCounter.current++;
          setMessages([...messages, {
            id: errorMessageId,
            role: "assistant",
            content: "I'm sorry, I encountered an error processing your request. Please try again or contact support if the issue persists."
          }]);
        }
        
        handleError(
          error,
          "Failed to get a response from assistant",
          ErrorSeverity.ERROR,
          { operation: "helpAssistant", additionalInfo: { attempts: retryAttempt + 1 } }
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [messages, retryAttempt, user, setMessages, messageIdCounter]);

  return {
    isLoading,
    networkError,
    retryAttempt,
    lastMessageRef,
    handleSendMessage
  };
};
